import { TableRow, TableCell, IconButton, Box, Typography, Grid, useTheme, useMediaQuery } from "@mui/material";
import React, { useState } from "react";
import { OrderDetails } from "../../../../../models/DB";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { formatTimestamp, getDeliveryTypeSetup, getReferenceValue } from "../../../../../utils/customFunctions";
import { OrderEditor } from "./OrderEditor";
import { OrderStatusBadgeCompact } from "../../../../../components/OrderStatusBadge";
import { isLarge } from "../../../../../navigation/HomeStack";
import { useStoreConfig } from "../../../../../contexts/StoreConfigContext";

interface OrderRowProps {
    order: OrderDetails;
}

export const OrderRow = ({ order }: OrderRowProps) => {
    const theme = useTheme();
    const { config } = useStoreConfig();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const IconToShow = () => {
        return open ? <KeyboardArrowUp /> : <KeyboardArrowDown />;
    };

    const DeliverTypeBlock = ({ deliveryType, compact = false }: { deliveryType: string; compact?: boolean }) => {
        const { backgroundColor, borderColor, textColor, text } = getDeliveryTypeSetup(deliveryType, theme, isLarge());

        return (
            <Grid
                item
                sx={{
                    display: "inline-block",
                    border: 1,
                    borderColor: borderColor,
                    backgroundColor: backgroundColor,
                    borderRadius: compact ? 1.5 : 2,
                    padding: compact ? 1 : 1.5,
                    paddingY: compact ? 0.25 : 0.5
                }}
            >
                <Typography
                    color={textColor}
                    fontSize={compact ? 9 : 11}
                    fontWeight={"bold"}
                    letterSpacing={0.5}
                    textAlign={"center"}
                    fontFamily={"primaryFont"}
                >{`${text}`}</Typography>
            </Grid>
        );
    };

    // Format short date for mobile
    const formatShortDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    return (
        <React.Fragment>
            <TableRow
                sx={{
                    "& > *": { borderBottom: "unset" },
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? theme.palette.action.hover
                            : theme.palette.grey[50]
                    }
                }}
            >
                {/* Column 1: Order Ref + Date/Total on mobile */}
                <TableCell component="th" scope="order">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            size="small"
                            onClick={() => setOpen(!open)}
                            sx={{ flexDirection: 'column' }}
                        >
                            <IconToShow />
                        </IconButton>
                        <Box sx={{ ml: isMobile ? 1 : 4 }}>
                            <Typography variant="body2"
                                sx={{
                                    fontSize: isMobile ? 11 : 12,
                                    fontFamily: "primaryFont",
                                    letterSpacing: 0.5,
                                    fontWeight: 600
                                }}>
                                #{getReferenceValue(order.reference)}
                            </Typography>
                            {/* Mobile: Show date and total below ref */}
                            {isMobile && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: "primaryFont",
                                        color: theme.palette.text.secondary,
                                        display: 'block',
                                        mt: 0.25
                                    }}
                                >
                                    {order.created ? formatShortDate(order.created) : ''} • {order.totalCost ? `${config.currency}${order.totalCost.toFixed(0)}` : 'N/A'}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </TableCell>

                {/* Mobile: Combined Status + Delivery Type column */}
                {isMobile ? (
                    <TableCell align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                            <OrderStatusBadgeCompact status={order.status || 1} size="small" />
                            <DeliverTypeBlock deliveryType={order.deliveryType} compact />
                        </Box>
                    </TableCell>
                ) : (
                    <>
                        {/* Desktop: Separate columns */}
                        <TableCell>
                            <OrderStatusBadgeCompact status={order.status || 1} />
                        </TableCell>
                        <TableCell>
                            <DeliverTypeBlock deliveryType={order.deliveryType} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>{order.email}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>
                            {order.totalCost ? `${config.currency}${order.totalCost.toFixed(2)}` : "N/A"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontFamily: "primaryFont", letterSpacing: 0.5 }}>
                            {order.created ? formatTimestamp(order.created) : "N/A"}
                        </TableCell>
                    </>
                )}
            </TableRow>
            {
                <OrderEditor
                    order={order}
                    open={open}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                />
            }
        </React.Fragment>
    );
};